import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { meetingsAPI } from '../../lib/api';
import { fmtDateOnly, apiErrorText } from './meetingsUtils';

function ActionRow({ a, onChanged }) {
  const [outcome, setOutcome] = useState('');
  const [closing, setClosing] = useState(false);
  const navigate = useNavigate();
  const overdue = a.status === 'OPEN' && a.due_date < new Date().toISOString().slice(0, 10);

  const close = async (newStatus) => {
    if (!outcome.trim()) { toast.error(newStatus === 'DONE' ? 'Please add the outcome' : 'Please add a reason'); return; }
    setClosing(true);
    try {
      await meetingsAPI.closeAction(a.id, { status: newStatus, outcome });
      toast.success(newStatus === 'DONE' ? 'Marked done' : 'Cancelled');
      onChanged();
    } catch (e) { toast.error(apiErrorText(e, 'Failed to update')); }
    finally { setClosing(false); }
  };

  return (
    <Card className="rounded-sm" data-testid={`action-item-${a.id}`}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-medium">{a.text}</p>
            <button className="text-xs text-muted-foreground underline" onClick={() => navigate(`/meetings/${a.meeting_id}`)}>
              {a.company?.name} · Due {fmtDateOnly(a.due_date)} · {a.promised_by === 'VSTL' ? 'We promised' : 'They promised'}
            </button>
          </div>
          <Badge variant="outline" className={overdue ? 'bg-red-100 text-red-800 border-red-300' : ''}>{a.status}</Badge>
        </div>
        {a.status === 'OPEN' && (
          <div className="flex items-center gap-2">
            <Textarea className="h-8 text-xs" placeholder="Outcome / reason" value={outcome} onChange={(e) => setOutcome(e.target.value)}
              data-testid={`action-outcome-input-${a.id}`} />
            <Button size="sm" disabled={closing} onClick={() => close('DONE')} data-testid={`action-done-btn-${a.id}`}>Done</Button>
            <Button size="sm" variant="outline" disabled={closing} onClick={() => close('CANCELLED')} data-testid={`action-cancel-btn-${a.id}`}>Cancel</Button>
          </div>
        )}
        {a.status !== 'OPEN' && a.outcome && <p className="text-xs text-muted-foreground">Outcome: {a.outcome}</p>}
      </CardContent>
    </Card>
  );
}

export default function ActionItemsTab({ isManager }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('mine');
  const [status, setStatus] = useState('OPEN');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await meetingsAPI.listActions({ view, status: status === 'all' ? undefined : status });
      setActions(res.data.actions || []);
    } catch { toast.error('Failed to load action items'); }
    finally { setLoading(false); }
  }, [view, status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="action-items-tab">
      <div className="flex items-center gap-2 flex-wrap">
        {isManager && (
          <Select value={view} onValueChange={setView}>
            <SelectTrigger className="w-36" data-testid="action-items-view-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="mine">Mine</SelectItem><SelectItem value="team">My team</SelectItem></SelectContent>
          </Select>
        )}
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-36" data-testid="action-items-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="OPEN">Open</SelectItem>
            <SelectItem value="DONE">Done</SelectItem>
            <SelectItem value="CANCELLED">Cancelled</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin" /></div>
      ) : actions.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8" data-testid="no-action-items-msg">No action items found.</p>
      ) : (
        <div className="space-y-2">{actions.map((a) => <ActionRow key={a.id} a={a} onChanged={load} />)}</div>
      )}
    </div>
  );
}
